import "@/App.css";
import { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import NewIncident from "@/pages/NewIncident";
import IncidentList from "@/pages/IncidentList";
import IncidentDetail from "@/pages/IncidentDetail";
import IncidentPrint from "@/pages/IncidentPrint";
import EditIncident from "@/pages/EditIncident";
import UserManagement from "@/pages/UserManagement";
import CompanyManagement from "@/pages/CompanyManagement";
import FormBuilder from "@/pages/FormBuilder";
import NotificationRouting from "@/pages/NotificationRouting";
import AuditLog from "@/pages/AuditLog";
import Impressum from "@/pages/Impressum";
import Datenschutz from "@/pages/Datenschutz";
import TechnischeDoku from "@/pages/TechnischeDoku";
import AVVertrag from "@/pages/AVVertrag";
import OperatorHandbook from "@/pages/OperatorHandbook";

// Set axios default header
const setAuthHeader = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

// Global logout function (will be set by App component)
let globalLogout = () => {};

// Axios interceptor to handle 401 responses
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token and trigger logout
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setAuthHeader(null);
      globalLogout();
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleLogout = useCallback(() => {
    setUser(null);
    setToken(null);
    setAuthHeader(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }, []);

  // Set global logout function
  useEffect(() => {
    globalLogout = handleLogout;
  }, [handleLogout]);

  useEffect(() => {
    // Check for existing session
    const savedToken = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    
    if (savedToken && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setToken(savedToken);
        setUser(parsedUser);
        setAuthHeader(savedToken);
      } catch (e) {
        // Invalid data, clear storage
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    setAuthHeader(authToken);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not logged in - show login page
  if (!user || !token) {
    return (
      <div className="app-container">
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/passwort-vergessen" element={<ResetPassword />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/technische-dokumentation" element={<TechnischeDoku />} />
            <Route path="/av-vertrag" element={<AVVertrag />} />
            <Route path="*" element={<Login onLogin={handleLogin} />} />
          </Routes>
        </BrowserRouter>
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#18181B',
              border: '1px solid #27272A',
              color: '#FAFAFA',
            },
          }}
        />
      </div>
    );
  }

  // Check if user is super-admin (admin without company)
  const isSuperAdmin = user.is_admin && !user.company_id;

  // Logged in - show app
  return (
    <div className="app-container">
      <BrowserRouter>
        <Layout user={user} onLogout={handleLogout}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/neu" element={<NewIncident />} />
            <Route path="/unfaelle" element={<IncidentList />} />
            <Route path="/unfall/:id" element={<IncidentDetail />} />
            <Route path="/unfall/:id/edit" element={<EditIncident />} />
            <Route path="/unfall/:id/drucken" element={<IncidentPrint />} />
            {isSuperAdmin && (
              <>
                <Route path="/unternehmen" element={<CompanyManagement token={token} />} />
                <Route path="/unternehmen/:companyId/email-ketten" element={<NotificationRouting />} />
                <Route path="/benutzer" element={<UserManagement token={token} />} />
                <Route path="/formulare" element={<FormBuilder token={token} />} />
                <Route path="/audit-log" element={<AuditLog token={token} />} />
                <Route path="/betreiberhandbuch" element={<OperatorHandbook />} />
              </>
            )}
            <Route path="/technische-dokumentation" element={<TechnischeDoku />} />
            <Route path="/av-vertrag" element={<AVVertrag />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </BrowserRouter>
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: '#18181B',
            border: '1px solid #27272A',
            color: '#FAFAFA',
          },
        }}
      />
    </div>
  );
}

export default App;
